import { useEffect, useMemo } from 'react';
import {
  Dropdown,
  InlineLoading,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { FreeTextComboBox } from 'components/free-text-combobox/free-text-combobox';
import { AreaDetailSection } from 'components/area-detail-section';
import { useAtomValue, useSetAtom } from 'jotai';
import { capitalize, debounce } from 'lodash-es';
import {
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  areaCategoryAtom,
  areaQueryAtom,
  areaSearchLoadableAtom,
  unitTypeAtom,
  unitQueryAtom,
  unitSearchLoadableAtom,
  wellQueryAtom,
  wellSearchLoadableAtom,
  stateQueryAtom,
  stateSearchLoadableAtom,
  fetchGeometryLoadableAtom,
  areaGeometriesAtom,
  toastAtom,
} from './atoms';
import type { BaseAreaSelection, ScenarioAreaConfigsProps } from './types';
import styles from './scenario-area-configs.module.scss';

const GEOMETRY_TYPE_LABELS: Record<string, string> = {
  area: 'Area',
  unit: 'Unit',
  slot: 'Slot',
  county: 'County',
  state: 'State',
};

/**
 * Shared area configuration form section for all scenario types.
 * Renders geometry type picker (filtered by enabledTypes), typeahead
 * search fields, alias, and notes. Owns all area search API calls.
 * @param {ScenarioAreaConfigsProps} props - The component props
 * @return {JSX.Element} The area configs form
 */
export function ScenarioAreaConfigs({
  form,
  areaIndex,
  enabledTypes,
  notesPlaceholder = 'Notes about this area...',
  collapsible = false,
}: ScenarioAreaConfigsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const setGeometries = useSetAtom(areaGeometriesAtom);

  return (
    <form.Subscribe
      selector={(state: any) => ({
        alias: state.values.areas[areaIndex]?.alias ?? '',
        notes: state.values.areas[areaIndex]?.notes ?? '',
        geometryType: state.values.areas[areaIndex]?.geometryType,
      })}
    >
      {({
        alias,
        notes,
        geometryType,
      }: {
        alias: string;
        notes: string;
        geometryType: string;
      }) => (
        <AreaDetailSection
          idPrefix={`area-${areaIndex}`}
          aliasValue={alias}
          onAliasChange={(v) => form.setFieldValue(`${prefix}.alias`, v)}
          notesValue={notes}
          onNotesChange={(v) => form.setFieldValue(`${prefix}.notes`, v)}
          notesPlaceholder={notesPlaceholder}
          collapsible={collapsible}
        >
          <form.Field name={`${prefix}.geometryType`}>
            {(field: any) => (
              <RadioButtonGroup
                legendText="Geometry Type"
                name={`geometry-type-${areaIndex}`}
                valueSelected={field.state.value}
                orientation="horizontal"
                onChange={(value: string | number | undefined) => {
                  const entryId = form.getFieldValue(`${prefix}.id`);
                  field.handleChange(value);
                  form.setFieldValue(`${prefix}.configurableId`, null);
                  form.setFieldValue(`${prefix}.category`, '');
                  form.setFieldValue(`${prefix}.areaName`, '');
                  form.setFieldValue(`${prefix}.unitType`, '');
                  form.setFieldValue(`${prefix}.unitName`, '');
                  form.setFieldValue(`${prefix}.slotUwi`, '');
                  form.setFieldValue(`${prefix}.slotName`, '');
                  if (entryId) {
                    setGeometries((prev) => {
                      const next = { ...prev };
                      delete next[entryId];
                      return next;
                    });
                  }
                }}
              >
                {enabledTypes.map((type) => (
                  <RadioButton
                    key={type}
                    id={`geom-${type}-${areaIndex}`}
                    labelText={GEOMETRY_TYPE_LABELS[type] || type}
                    value={type}
                  />
                ))}
              </RadioButtonGroup>
            )}
          </form.Field>

          {geometryType === 'area' && (
            <AreaTypeFields form={form} areaIndex={areaIndex} />
          )}
          {geometryType === 'unit' && (
            <UnitTypeFields form={form} areaIndex={areaIndex} />
          )}
          {geometryType === 'slot' && (
            <SlotTypeFields form={form} areaIndex={areaIndex} />
          )}
          {geometryType === 'state' && (
            <StateTypeFields form={form} areaIndex={areaIndex} />
          )}
        </AreaDetailSection>
      )}
    </form.Subscribe>
  );
}

// ─────────────────────────────────────
// Area geometry fields
// ─────────────────────────────────────

interface TypeFieldsProps {
  form: any;
  areaIndex: number;
}

/**
 * Area-type fields: category dropdown + area ComboBox with typeahead search.
 * @param {TypeFieldsProps} props - The form and area index
 * @return {JSX.Element} The area type fields
 */
function AreaTypeFields({ form, areaIndex }: TypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const areaSearchState = useAtomValue(areaSearchLoadableAtom);
  const setAreaCategory = useSetAtom(areaCategoryAtom);
  const setAreaQuery = useSetAtom(areaQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);
  const setToast = useSetAtom(toastAtom);

  useEffect(() => {
    if (areaSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search areas', type: 'error' });
    }
  }, [areaSearchState.state, setToast]);

  const categoryItems =
    categoriesState.state === 'hasData' && categoriesState.data
      ? categoriesState.data.categories
      : [];

  const debouncedAreaQuery = useMemo(
    () => debounce((text: string) => setAreaQuery(text), 350),
    [setAreaQuery]
  );

  useEffect(() => {
    return () => debouncedAreaQuery.cancel();
  }, [debouncedAreaQuery]);

  const areaItems = useMemo(() => {
    if (areaSearchState.state !== 'hasData' || !areaSearchState.data) return [];
    return areaSearchState.data.results.map((r) => {
      const label =
        [r.purpose, r.name].filter(Boolean).join(' - ') || r.displayName;
      return { id: r.id, value: label, label };
    });
  }, [areaSearchState]);

  return (
    <div className={styles.formGrid}>
      {categoriesState.state === 'loading' ? (
        <InlineLoading description="Loading categories..." />
      ) : (
        <form.Field name={`${prefix}.category`}>
          {(field: any) => (
            <Dropdown
              id={`area-category-${areaIndex}`}
              titleText="Area Category (required)"
              label="Choose a category"
              size="md"
              light
              items={categoryItems}
              selectedItem={field.state.value || null}
              onChange={({ selectedItem }: { selectedItem: string }) => {
                const entryId = form.getFieldValue(`${prefix}.id`);
                field.handleChange(selectedItem || '');
                form.setFieldValue(`${prefix}.areaName`, '');
                form.setFieldValue(`${prefix}.configurableId`, null);
                setAreaCategory(selectedItem || '');
                setAreaQuery(undefined);
                if (entryId) {
                  setGeometries((prev) => {
                    const next = { ...prev };
                    delete next[entryId];
                    return next;
                  });
                }
              }}
            />
          )}
        </form.Field>
      )}

      <form.Subscribe
        selector={(state: any) => ({
          entryId: state.values.areas[areaIndex]?.id,
          category: state.values.areas[areaIndex]?.category,
          allAreas: state.values.areas,
        })}
      >
        {({
          entryId,
          category,
          allAreas,
        }: {
          entryId: number;
          category: string;
          allAreas: BaseAreaSelection[];
        }) => {
          // Dedupe "already used" areas by the stable area id, not the display
          // name: two distinct areas can share a name, and the catalog lists one
          // current version per business area, so the id is the reliable identity.
          // (a.areaName keeps this scoped to area-type selections.)
          const usedAreaIds = allAreas
            .filter((_: BaseAreaSelection, i: number) => i !== areaIndex)
            .filter((a: BaseAreaSelection) => a.areaName)
            .map((a: BaseAreaSelection) => a.configurableId)
            .filter((id: number | null): id is number => id !== null);

          const filteredItems = areaItems.filter(
            (item) => !usedAreaIds.includes(item.id)
          );

          return (
            <div
              className={styles.typeaheadWrapper}
              onFocus={() => {
                if (
                  category &&
                  areaSearchState.state !== 'loading' &&
                  !areaItems.length
                ) {
                  setAreaCategory(category);
                  setAreaQuery('');
                }
              }}
            >
              <form.Field name={`${prefix}.areaName`}>
                {(field: any) => (
                  <FreeTextComboBox
                    id={`area-selection-${areaIndex}`}
                    titleText="Area (required)"
                    placeholder="Search areas..."
                    items={filteredItems}
                    selectedItem={
                      field.state.value
                        ? { value: field.state.value, label: field.state.value }
                        : null
                    }
                    itemToString={(item) => item?.label ?? ''}
                    itemToElement={(item) => (
                      <span className={styles.areaMenuItem}>{item.label}</span>
                    )}
                    onChange={({ selectedItem }) => {
                      field.handleChange(selectedItem?.value ?? '');
                      form.setFieldValue(
                        `${prefix}.configurableId`,
                        selectedItem?.id ?? null
                      );
                      if (selectedItem?.id && entryId) {
                        fetchGeometry({
                          entryId,
                          type: 'area',
                          configurableId: selectedItem.id,
                        }).catch(() => {
                          field.handleChange('');
                          form.setFieldValue(`${prefix}.configurableId`, null);
                        });
                      } else if (entryId) {
                        setGeometries((prev) => {
                          const next = { ...prev };
                          delete next[entryId];
                          return next;
                        });
                      }
                    }}
                    onInputChange={(text) => debouncedAreaQuery(text)}
                  />
                )}
              </form.Field>
              {areaSearchState.state === 'loading' && (
                <InlineLoading description="" className={styles.spinner} />
              )}
            </div>
          );
        }}
      </form.Subscribe>
    </div>
  );
}

// ─────────────────────────────────────
// Unit geometry fields
// ─────────────────────────────────────

/**
 * Unit-type fields: unit type dropdown + unit name ComboBox with typeahead.
 * @param {TypeFieldsProps} props - The form and area index
 * @return {JSX.Element} The unit type fields
 */
function UnitTypeFields({ form, areaIndex }: TypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);
  const unitSearchState = useAtomValue(unitSearchLoadableAtom);
  const setUnitType = useSetAtom(unitTypeAtom);
  const setUnitQuery = useSetAtom(unitQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);
  const setToast = useSetAtom(toastAtom);

  useEffect(() => {
    if (unitSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search units', type: 'error' });
    }
  }, [unitSearchState.state, setToast]);

  const unitTypeItems =
    unitTypesState.state === 'hasData' && unitTypesState.data
      ? unitTypesState.data.unit_types
      : [];

  const debouncedUnitQuery = useMemo(
    () => debounce((text: string) => setUnitQuery(text), 350),
    [setUnitQuery]
  );

  useEffect(() => {
    return () => debouncedUnitQuery.cancel();
  }, [debouncedUnitQuery]);

  const unitItems = useMemo(() => {
    if (unitSearchState.state !== 'hasData' || !unitSearchState.data) return [];
    return unitSearchState.data.results.map((r) => ({
      id: r.id,
      value: r.name,
      label: r.name,
    }));
  }, [unitSearchState]);

  return (
    <div className={styles.formGrid}>
      {unitTypesState.state === 'loading' ? (
        <InlineLoading description="Loading unit types..." />
      ) : (
        <form.Field name={`${prefix}.unitType`}>
          {(field: any) => (
            <Dropdown
              id={`unit-type-${areaIndex}`}
              titleText="Unit Type (required)"
              label="Choose an option"
              size="md"
              light
              items={unitTypeItems}
              selectedItem={field.state.value || null}
              itemToString={(item: string) => (item ? capitalize(item) : '')}
              onChange={({ selectedItem }: { selectedItem: string }) => {
                const entryId = form.getFieldValue(`${prefix}.id`);
                field.handleChange(selectedItem || '');
                form.setFieldValue(`${prefix}.unitName`, '');
                form.setFieldValue(`${prefix}.configurableId`, null);
                setUnitType(selectedItem || '');
                setUnitQuery(undefined);
                if (entryId) {
                  setGeometries((prev) => {
                    const next = { ...prev };
                    delete next[entryId];
                    return next;
                  });
                }
              }}
            />
          )}
        </form.Field>
      )}

      <form.Subscribe
        selector={(state: any) => ({
          entryId: state.values.areas[areaIndex]?.id,
          unitType: state.values.areas[areaIndex]?.unitType,
        })}
      >
        {({ entryId, unitType }: { entryId: number; unitType: string }) => (
          <div
            className={styles.typeaheadWrapper}
            onFocus={() => {
              if (
                unitType &&
                unitSearchState.state !== 'loading' &&
                !unitItems.length
              ) {
                setUnitType(unitType);
                setUnitQuery('');
              }
            }}
          >
            <form.Field name={`${prefix}.unitName`}>
              {(field: any) => (
                <FreeTextComboBox
                  id={`unit-name-${areaIndex}`}
                  titleText="Unit Name (required)"
                  placeholder="Search units..."
                  items={unitItems}
                  selectedItem={
                    field.state.value
                      ? { value: field.state.value, label: field.state.value }
                      : null
                  }
                  itemToString={(item) => item?.label ?? ''}
                  onChange={({ selectedItem }) => {
                    field.handleChange(selectedItem?.value ?? '');
                    form.setFieldValue(
                      `${prefix}.configurableId`,
                      selectedItem?.id ?? null
                    );
                    if (selectedItem?.id && entryId) {
                      fetchGeometry({
                        entryId,
                        type: 'unit',
                        configurableId: selectedItem.id,
                      }).catch(() => {
                        field.handleChange('');
                        form.setFieldValue(`${prefix}.configurableId`, null);
                      });
                    } else if (entryId) {
                      setGeometries((prev) => {
                        const next = { ...prev };
                        delete next[entryId];
                        return next;
                      });
                    }
                  }}
                  onInputChange={(text) => debouncedUnitQuery(text)}
                />
              )}
            </form.Field>
            {unitSearchState.state === 'loading' && (
              <InlineLoading description="" className={styles.spinner} />
            )}
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}

// ─────────────────────────────────────
// Slot (well) geometry fields
// ─────────────────────────────────────

/**
 * Slot-type fields: well UWI ComboBox with typeahead search.
 * Wells have no polygon geometry — configurable_geojson is null.
 * @param {TypeFieldsProps} props - The form and area index
 * @return {JSX.Element} The slot type fields
 */
function SlotTypeFields({ form, areaIndex }: TypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const wellSearchState = useAtomValue(wellSearchLoadableAtom);
  const setWellQuery = useSetAtom(wellQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setToast = useSetAtom(toastAtom);

  useEffect(() => {
    if (wellSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search wells', type: 'error' });
    }
  }, [wellSearchState.state, setToast]);

  const debouncedWellQuery = useMemo(
    () => debounce((text: string) => setWellQuery(text), 350),
    [setWellQuery]
  );

  useEffect(() => {
    return () => debouncedWellQuery.cancel();
  }, [debouncedWellQuery]);

  const wellItems = useMemo(() => {
    if (wellSearchState.state !== 'hasData' || !wellSearchState.data) return [];
    return wellSearchState.data.results.map((r) => {
      const label = r.name ? `${r.uwi} — ${r.name}` : r.uwi;
      return { id: r.id, value: label, label };
    });
  }, [wellSearchState]);

  return (
    <div className={styles.slotField}>
      <form.Subscribe
        selector={(state: any) => ({
          entryId: state.values.areas[areaIndex]?.id,
        })}
      >
        {({ entryId }: { entryId: number }) => (
          <div className={styles.typeaheadWrapper}>
            <form.Field name={`${prefix}.slotName`}>
              {(field: any) => (
                <FreeTextComboBox
                  id={`slot-uwi-${areaIndex}`}
                  titleText="Slot (required)"
                  placeholder="Search by UWI or well name..."
                  items={wellItems}
                  selectedItem={
                    field.state.value
                      ? { value: field.state.value, label: field.state.value }
                      : null
                  }
                  itemToString={(item) => item?.label ?? ''}
                  onChange={({ selectedItem }) => {
                    field.handleChange(selectedItem?.value ?? '');
                    form.setFieldValue(
                      `${prefix}.configurableId`,
                      selectedItem?.id ?? null
                    );
                    const uwi = selectedItem?.value?.split(' — ')[0] ?? '';
                    form.setFieldValue(`${prefix}.slotUwi`, uwi);
                    if (selectedItem?.id && entryId) {
                      fetchGeometry({
                        entryId,
                        type: 'slot',
                        configurableId: selectedItem.id,
                        uwi,
                      }).catch(() => {
                        field.handleChange('');
                        form.setFieldValue(`${prefix}.configurableId`, null);
                        form.setFieldValue(`${prefix}.slotUwi`, '');
                      });
                    }
                  }}
                  onInputChange={(text) => debouncedWellQuery(text)}
                />
              )}
            </form.Field>
            {wellSearchState.state === 'loading' && (
              <InlineLoading description="" className={styles.spinner} />
            )}
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}

// ─────────────────────────────────────
// State geometry fields
// ─────────────────────────────────────

/**
 * State-type fields: state ComboBox with debounced typeahead search.
 * States have polygon geometry — matched via PostGIS spatial intersection.
 * @param {TypeFieldsProps} props - The form and area index
 * @return {JSX.Element} The state type fields
 */
function StateTypeFields({ form, areaIndex }: TypeFieldsProps): JSX.Element {
  const prefix = `areas[${areaIndex}]` as const;
  const stateSearchState = useAtomValue(stateSearchLoadableAtom);
  const setStateQuery = useSetAtom(stateQueryAtom);
  const fetchGeometry = useSetAtom(fetchGeometryLoadableAtom);
  const setToast = useSetAtom(toastAtom);

  useEffect(() => {
    if (stateSearchState.state === 'hasError') {
      setToast({ message: 'Failed to search states', type: 'error' });
    }
  }, [stateSearchState.state, setToast]);

  const debouncedStateQuery = useMemo(
    () => debounce((text: string) => setStateQuery(text), 350),
    [setStateQuery]
  );

  useEffect(() => {
    return () => debouncedStateQuery.cancel();
  }, [debouncedStateQuery]);

  const stateItems = useMemo(() => {
    if (stateSearchState.state !== 'hasData' || !stateSearchState.data) {
      return [];
    }
    return stateSearchState.data.results.map((s) => ({
      id: s.id,
      value: s.name,
      label: `${s.name} (${s.abbreviation})`,
    }));
  }, [stateSearchState]);

  return (
    <div className={styles.formGrid}>
      <form.Subscribe
        selector={(state: any) => ({
          entryId: state.values.areas[areaIndex]?.id,
        })}
      >
        {({ entryId }: { entryId: number }) => (
          <div
            className={styles.typeaheadWrapper}
            onFocus={() => {
              if (stateSearchState.state !== 'loading' && !stateItems.length) {
                setStateQuery('');
              }
            }}
          >
            <form.Field name={`${prefix}.stateName`}>
              {(field: any) => (
                <FreeTextComboBox
                  id={`state-selection-${areaIndex}`}
                  titleText="State (required)"
                  placeholder="Search states..."
                  items={stateItems}
                  selectedItem={
                    field.state.value
                      ? { value: field.state.value, label: field.state.value }
                      : null
                  }
                  itemToString={(item) => item?.label ?? ''}
                  onChange={({ selectedItem }) => {
                    field.handleChange(selectedItem?.value ?? '');
                    form.setFieldValue(
                      `${prefix}.configurableId`,
                      selectedItem?.id ?? null
                    );
                    if (selectedItem?.id && entryId) {
                      fetchGeometry({
                        entryId,
                        type: 'state',
                        configurableId: selectedItem.id,
                      }).catch(() => {
                        field.handleChange('');
                        form.setFieldValue(`${prefix}.configurableId`, null);
                      });
                    }
                  }}
                  onInputChange={(text) => debouncedStateQuery(text)}
                />
              )}
            </form.Field>
            {stateSearchState.state === 'loading' && (
              <InlineLoading description="" className={styles.spinner} />
            )}
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}
